/**
 * The4 / Kalles port of giftifymama.shop size chart.
 * Plain custom element — no @theme/component, no on:click.
 * Mounts into [data-gfm-size-chart-slot] via the Liquid snippet.
 * GEO Fix #11: compatible with Liquid SSR tables inside [data-gfm-content].
 */
(function () {
  const chartUnits = ['cm', 'inches'];
  const numericCell = /^-?\d+(?:\.\d+)?$/;

  function splitLines(text) {
    if (typeof text !== 'string') return [];
    return text.split(/\r?\n/).map((line) => line.trim()).filter((line) => line !== '');
  }
  function splitCells(line) {
    return line.split(/[|｜]/).map((cell) => cell.trim());
  }
  function fitRow(cells, size) {
    const row = cells.slice(0, size);
    while (row.length < size) row.push('');
    return row;
  }
  // C7 rounding rule (friendly half-inch):
  //   inches display → nearest 0.5 (39.76→40, 26.97→27, 32.87→33, 39.4→39.5)
  //   cm display (from inches) → 1 decimal (unchanged)
  function roundToHalfInch(n) {
    return Math.round(Number(n) * 2) / 2;
  }
  function friendlyRoundInchRow(row) {
    return row.map((cell, index) => {
      if (index === 0 || !numericCell.test(cell)) return cell;
      return String(roundToHalfInch(cell));
    });
  }
  function convertRow(row, factor) {
    return row.map((cell, index) => {
      if (index === 0 || !numericCell.test(cell)) return cell;
      const n = Number(cell) * factor;
      // cm→inches (factor < 1): nearest 0.5; inches→cm: 1 decimal
      const value = factor < 1 ? roundToHalfInch(n) : Math.round(n * 10) / 10;
      return String(value);
    });
  }

  function buildChartFromText(raw) {
    if (!raw || !Array.isArray(raw.tables)) return null;
    const tables = [];
    for (const source of raw.tables) {
      const columns = splitLines(source?.columns).flatMap(splitCells).filter((cell) => cell !== '');
      let inches = splitLines(source?.inches).map((line) => fitRow(splitCells(line), columns.length));
      let cm = splitLines(source?.cm).map((line) => fitRow(splitCells(line), columns.length));
      if (columns.length < 2 || (inches.length === 0 && cm.length === 0)) continue;
      if (cm.length === 0) cm = inches.map((row) => convertRow(row, 2.54));
      if (inches.length === 0) inches = cm.map((row) => convertRow(row, 1 / 2.54));
      const rows = { cm, inches };
      if (cm.length !== inches.length || cm.some((row, index) => row[0] !== inches[index][0])) {
        rows.inches = cm.map((row) => convertRow(row, 1 / 2.54));
      }
      // Always half-round inches — metafield often ships both cm+inches (convertRow skipped)
      rows.inches = rows.inches.map(friendlyRoundInchRow);
      const title = typeof source?.title === 'string' ? source.title.trim() : '';
      tables.push({ title, columns, rows });
    }
    if (tables.length === 0) return null;
    return {
      tables,
      measurementInstructions: splitLines(raw.instructions),
      notes: splitLines(raw.notes),
    };
  }

  function hasValidRows(table, unit) {
    const rows = table.rows?.[unit];
    return (
      Array.isArray(rows) &&
      rows.length > 0 &&
      rows.every(
        (row) =>
          Array.isArray(row) &&
          row.length === table.columns.length &&
          row.every((cell) => typeof cell === 'string')
      )
    );
  }

  function isValidTable(table) {
    const valid =
      typeof table?.title === 'string' &&
      Array.isArray(table.columns) &&
      table.columns.length > 1 &&
      table.columns.every((column) => typeof column === 'string') &&
      chartUnits.some((unit) => hasValidRows(table, unit));
    if (!valid) return false;
    if (hasValidRows(table, 'cm') && hasValidRows(table, 'inches')) {
      const cmRows = table.rows.cm;
      const inchRows = table.rows.inches;
      return cmRows.length === inchRows.length && cmRows.every((row, index) => row[0] === inchRows[index][0]);
    }
    return true;
  }

  function normalizeChart(chart) {
    if (!chart || !Array.isArray(chart.tables) || chart.tables.length === 0) return null;
    if (!chart.tables.every(isValidTable)) return null;
    const units = chartUnits.filter((unit) => chart.tables.every((table) => hasValidRows(table, unit)));
    if (units.length === 0) return null;
    const tables = chart.tables.map((table) => {
      if (!hasValidRows(table, 'inches')) return table;
      return {
        ...table,
        rows: { ...table.rows, inches: table.rows.inches.map(friendlyRoundInchRow) },
      };
    });
    return { ...chart, tables, units };
  }

  function appendTextLines(parent, lines, className) {
    if (!Array.isArray(lines)) return;
    for (const line of lines) {
      if (typeof line !== 'string' || line.trim() === '') continue;
      const paragraph = document.createElement('p');
      paragraph.className = className;
      paragraph.textContent = line;
      parent.append(paragraph);
    }
  }

  class GiftifyMamaSizeChart extends HTMLElement {
    constructor() {
      super();
      this._chart = null;
      this._unit = 'cm';
      this._opener = null;
    }

    connectedCallback() {
      this._bind();
      this._initialize();
    }

    disconnectedCallback() {
      this.closeDialog(false);
    }

    _qs(sel) {
      return this.querySelector(sel);
    }

    _bind() {
      const trigger = this._qs('[data-gfm-trigger]');
      const closeBtn = this._qs('[data-gfm-close]');
      const dialog = this._qs('dialog');
      trigger?.addEventListener('click', () => this.openDialog());
      closeBtn?.addEventListener('click', () => this.closeDialog());
      dialog?.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.closeDialog();
      });
      dialog?.addEventListener('click', (e) => {
        if (e.target === dialog) this.closeDialog();
      });
      this.querySelectorAll('[data-gfm-unit]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const unit = btn.getAttribute('data-gfm-unit');
          if (!this._chart?.units.includes(unit) || unit === this._unit) return;
          this._unit = unit;
          this._renderChart();
        });
      });
    }

    _initialize() {
      // Keep Liquid SSR tables in [data-gfm-content] until a valid chart hydrates.
      this.hidden = true;
      this._chart = null;
      const handle = this.dataset.productHandle;
      if (!handle) return;

      const textChart = this.querySelector('script[data-gfm-size-chart-text]');
      if (textChart) {
        try {
          const built = buildChartFromText(JSON.parse(textChart.textContent || ''));
          const chart = normalizeChart(built);
          if (chart) {
            this._applyChart(chart);
            return;
          }
        } catch (_) {}
      }

      const inlineChart = this.querySelector('script[data-gfm-size-chart-data]');
      if (inlineChart) {
        try {
          const chart = normalizeChart(JSON.parse(inlineChart.textContent || ''));
          if (chart) {
            this._applyChart(chart);
            return;
          }
        } catch (_) {}
      }
      // No valid chart: leave any SSR markup untouched (do not clear content).
    }

    _applyChart(chart) {
      this._chart = chart;
      const def = this.dataset.defaultUnit || 'cm';
      this._unit = chart.units.includes(def) ? def : chart.units[0];
      this._renderChart();
      this.hidden = false;
      // Place into slot if we were rendered as a sibling / elsewhere
      const slot = document.querySelector('[data-gfm-size-chart-slot]');
      if (slot && !slot.contains(this) && this.dataset.autoSlot !== 'false') {
        slot.appendChild(this);
      }
    }

    openDialog() {
      const dialog = this._qs('dialog');
      const trigger = this._qs('[data-gfm-trigger]');
      if (!this._chart || this.hidden || !dialog || dialog.open) return;
      this._opener = document.activeElement instanceof HTMLElement ? document.activeElement : trigger;
      dialog.showModal();
      trigger?.setAttribute('aria-expanded', 'true');
      this._qs('[data-gfm-close]')?.focus({ preventScroll: true });
      document.documentElement.style.overflow = 'hidden';
    }

    closeDialog(restoreFocus = true) {
      const dialog = this._qs('dialog');
      const trigger = this._qs('[data-gfm-trigger]');
      if (!(dialog instanceof HTMLDialogElement)) return;
      const wasOpen = dialog.open;
      if (wasOpen) dialog.close();
      document.documentElement.style.overflow = '';
      trigger?.setAttribute('aria-expanded', 'false');
      if (wasOpen && restoreFocus !== false && this._opener?.isConnected) {
        this._opener.focus({ preventScroll: true });
      }
      this._opener = null;
    }

    _renderChart() {
      if (!this._chart) return;
      const content = this._qs('[data-gfm-content]');
      const unitsEl = this._qs('[data-gfm-units]');
      const unitStatus = this._qs('[data-gfm-unit-status]');
      if (!content) return;
      // Never blank SSR/content when unit rows are missing.
      if (!this._chart.tables.every((table) => hasValidRows(table, this._unit))) return;
      const unitLabel = this._unit === 'cm' ? (this.dataset.unitCm || 'CM') : (this.dataset.unitInches || 'INCHES');

      this.querySelectorAll('[data-gfm-unit]').forEach((button) => {
        const u = button.getAttribute('data-gfm-unit');
        button.hidden = !this._chart.units.includes(u);
        button.setAttribute('aria-pressed', String(u === this._unit));
      });
      if (unitsEl) unitsEl.hidden = this._chart.units.length < 2;
      if (unitStatus) unitStatus.textContent = unitLabel;

      const fragment = document.createDocumentFragment();
      for (const table of this._chart.tables) {
        const section = document.createElement('section');
        section.className = 'gfm-size-chart__section';
        const heading = document.createElement('h3');
        heading.className = 'gfm-size-chart__table-heading';
        heading.textContent = table.title;
        section.append(heading);

        const wrapper = document.createElement('div');
        wrapper.className = 'gfm-size-chart__table-scroll';
        wrapper.tabIndex = 0;
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('aria-label', `${table.title} · ${unitLabel}`);
        wrapper.style.setProperty('--gfm-chart-columns', String(table.columns.length));
        wrapper.dataset.compact = String(table.columns.length <= 4);

        const tableElement = document.createElement('table');
        tableElement.className = 'gfm-size-chart__table';
        const thead = document.createElement('thead');
        const headerRow = document.createElement('tr');
        for (const column of table.columns) {
          const th = document.createElement('th');
          th.scope = 'col';
          th.textContent = column;
          headerRow.append(th);
        }
        thead.append(headerRow);
        tableElement.append(thead);

        const tbody = document.createElement('tbody');
        for (const row of table.rows[this._unit]) {
          const tr = document.createElement('tr');
          row.forEach((value, index) => {
            const cell = document.createElement(index === 0 ? 'th' : 'td');
            if (index === 0) cell.scope = 'row';
            cell.textContent = value;
            tr.append(cell);
          });
          tbody.append(tr);
        }
        tableElement.append(tbody);
        wrapper.append(tableElement);
        section.append(wrapper);
        appendTextLines(section, table.notes, 'gfm-size-chart__note');
        fragment.append(section);
      }

      appendTextLines(fragment, this._chart.notes, 'gfm-size-chart__note');
      if (Array.isArray(this._chart.measurementInstructions)) {
        const instructions = document.createElement('ul');
        instructions.className = 'gfm-size-chart__instructions';
        for (const line of this._chart.measurementInstructions) {
          if (typeof line !== 'string' || line.trim() === '') continue;
          const item = document.createElement('li');
          item.textContent = line;
          instructions.append(item);
        }
        if (instructions.children.length) fragment.append(instructions);
      }
      // Swap only after fragment is fully built (unit toggle + hydrate from same JSON).
      content.replaceChildren(fragment);
    }
  }

  if (!customElements.get('giftifymama-size-chart')) {
    customElements.define('giftifymama-size-chart', GiftifyMamaSizeChart);
  }

  // Expose for debugging / Liquid-free tests
  window.GFMSizeChart = { buildChartFromText, normalizeChart };
})();
